import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { features, directors, trade } from "../data/site";

const pillars = [
  {
    n: "01",
    title: "Certified Diamonds",
    text: "Natural and lab-grown diamonds, independently graded and certified by GIA, IGI and SGI for guaranteed quality.",
  },
  {
    n: "02",
    title: "Import & Export",
    text: "Two years of trading diamonds and premium CZ jewellery across the UK, US, Australia and Dubai.",
  },
  {
    n: "03",
    title: "Premium Craft",
    text: "From lab-grown and natural diamonds to premium CZ, each piece is finished to showcase every stone at its best.",
  },
];

const focusClass = {
  left: "object-[20%_top]",
  right: "object-[80%_top]",
  center: "object-top",
};

function DirectorCard({ d, delay }) {
  return (
    <Reveal delay={delay}>
      <div className="group">
        <div className="relative aspect-[4/5] overflow-hidden bg-cream-dark">
          {d.image ? (
            <img
              src={d.image}
              alt={d.name}
              className={`h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105 ${
                focusClass[d.focus] || "object-top"
              }`}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-charcoal">
              <span className="font-serif text-6xl font-light text-gold/70">
                {d.name.charAt(0)}
              </span>
              <span className="mt-3 font-sans text-[11px] uppercase tracking-[0.2em] text-cream/40">
                Portrait coming soon
              </span>
            </div>
          )}
        </div>
        <h3 className="mt-5 font-serif text-2xl font-light text-charcoal">{d.name}</h3>
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-gold-dark">
          {d.role}
        </p>
      </div>
    </Reveal>
  );
}

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="The Legacy"
        title="A house built on diamonds"
        subtitle="Culture Diamond Jewels Pvt. Ltd. — certified natural & lab-grown diamonds and premium CZ jewellery, from our home at Ten-11, C.G. Road, Ahmedabad."
        image="/assets/cd/model-emerald.jpg"
      />

      {/* Story */}
      <section className="py-24 md:py-28">
        <div className="container-luxe grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="overflow-hidden">
            <img
              src="/assets/about/directors.jpg"
              alt="Culture Diamond Jewels leadership"
              className="h-[420px] w-full object-cover object-top sm:h-[520px] lg:h-[600px]"
            />
          </Reveal>
          <Reveal delay={150}>
            <p className="eyebrow mb-5">Our Story</p>
            <h2 className="section-title mb-8">
              Diamonds as unique as{" "}
              <span className="italic text-gold-dark">you</span>
            </h2>
            <div className="space-y-5 font-sans text-base font-light leading-relaxed text-charcoal/70">
              <p>
                Culture Diamond Jewels was founded on a simple conviction: that a
                diamond should be as individual as the person who wears it. We bring
                together certified natural and lab-grown diamonds and premium CZ,
                each selected for exceptional cut, colour and clarity.
              </p>
              <p>
                From our showroom at Ten-11 on C.G. Road in the heart of Ahmedabad,
                we serve clients and partners across the world — and for the past
                two years we have imported and exported diamonds and CZ jewellery to
                the UK, US, Australia and Dubai.
              </p>
              <p>
                Every stone is independently certified by GIA, IGI or SGI, so the
                brilliance you see is brilliance you can trust.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Directors */}
      <section className="border-y border-gold/15 bg-cream py-24">
        <div className="container-luxe">
          <Reveal className="mb-16 text-center">
            <p className="eyebrow mb-5">Leadership</p>
            <h2 className="section-title">Our Directors</h2>
            <p className="mx-auto mt-5 max-w-xl font-sans text-sm font-light leading-relaxed text-charcoal/60">
              Culture Diamond Jewels is guided by three directors whose shared
              commitment to quality shapes every piece we make.
            </p>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {directors.map((d, i) => (
              <DirectorCard key={d.name} d={d} delay={i * 120} />
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24">
        <div className="container-luxe">
          <Reveal className="mb-16 text-center">
            <p className="eyebrow mb-5">What Sets Us Apart</p>
            <h2 className="section-title">The Culture Diamond standard</h2>
          </Reveal>
          <div className="grid gap-12 md:grid-cols-3">
            {pillars.map((p, i) => (
              <Reveal key={p.n} delay={i * 120}>
                <span className="font-serif text-5xl font-light text-gold/40">{p.n}</span>
                <h3 className="mb-3 mt-4 font-serif text-2xl font-light text-charcoal">{p.title}</h3>
                <p className="font-sans text-sm font-light leading-relaxed text-charcoal/60">
                  {p.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Showroom building */}
      <section className="border-t border-gold/15 bg-cream py-24">
        <div className="container-luxe grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="overflow-hidden">
            <img
              src="/assets/about/showroom-evening.jpg"
              alt="Ten-11, C.G. Road, Ahmedabad"
              className="h-[420px] w-full object-cover sm:h-[520px] lg:h-[600px]"
            />
          </Reveal>
          <Reveal delay={150}>
            <p className="eyebrow mb-5">Our Home</p>
            <h2 className="section-title mb-8">
              Ten-11, <span className="italic text-gold-dark">C.G. Road</span>
            </h2>
            <div className="space-y-5 font-sans text-base font-light leading-relaxed text-charcoal/70">
              <p>
                You'll find us at 405, Ten-11, Near Maradia Plaza, C.G. Road,
                Ahmedabad – 380006 — a landmark address in one of the city's most
                celebrated retail destinations.
              </p>
              <p>
                {trade.years} years of import and export experience means the
                diamonds and CZ jewellery you see are sourced and finished to
                international standards.
              </p>
            </div>
            <Link to="/contact" className="btn-gold mt-10">
              Plan Your Visit
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Promises */}
      <section className="py-24">
        <div className="container-luxe">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 90}>
                <div className="hairline mb-5" />
                <h3 className="mb-2 font-serif text-xl font-light text-charcoal">{f.title}</h3>
                <p className="font-sans text-sm font-light leading-relaxed text-charcoal/60">
                  {f.text}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16 text-center">
            <Link to="/contact" className="btn-gold">
              Visit the Showroom
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
